import { createClient, RealtimeChannel, SupabaseClient, type User } from '@supabase/supabase-js'
import { defineStore } from 'pinia'
import { usePhotos } from './photos'
import { useNotifications } from './notifications'
import { useNotificationsSettings } from './notifications-settings'
import { PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { ref, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import { useLocalStorage } from '@vueuse/core'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { useRouter } from 'vue-router'

const firebaseConfig = {
  apiKey: 'AIzaSyBiGKzrJdxnWvHE7fgTtmA9W0MggX4w0aU',
  authDomain: 'gaze-into-photography.firebaseapp.com',
  projectId: 'gaze-into-photography',
  storageBucket: 'gaze-into-photography.firebasestorage.app',
  messagingSenderId: '684857318695',
  appId: '1:684857318695:web:1466ea4e108fcade349d02',
  measurementId: 'G-LXQ5XH4ELP'
}

// Firebase will be initialized inside the store

export type AuthState =
  | {
      status: 'Unknown'
    }
  | {
      status: 'SignedIn'
      data: Profile
    }
  | {
      status: 'SignedOut'
    }

export type Profile = {
  id: string
  email: string
  avatar?: string
  username?: string
  banner_image?: string
  dark_mode?: boolean
  reduced_motion?: boolean
  language?: string
  notifications_enabled?: boolean
  user_created_at: string
  subscription_tier?: string
  subscription_status?: string
  has_used_free_trial?: boolean
  needs_onboarding?: boolean
  beta_code_used?: string
}

export const useSupabase = defineStore('supabase', () => {
  const photos = usePhotos()
  const notifications = useNotifications()
  const notificationsSettings = useNotificationsSettings()
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

  const supabase = createClient(supabaseUrl, supabaseKey)
  let notificationsChannel: RealtimeChannel | null = null

  const user = useLocalStorage<AuthState>('user-auth', { status: 'Unknown' })
  const loadedUserId = ref<string | null>(null)
  const currentDeviceToken = useLocalStorage<string | null>('device-fcm-token', null)

  // Get router instance for navigation
  const router = useRouter()

  // Initialize Firebase inside the store (only once)
  const firebaseApp = initializeApp(firebaseConfig)

  // Check if messaging is supported
  if (!('serviceWorker' in navigator)) {
    console.error('Service Worker not supported')
  }
  if (!('PushManager' in window)) {
    console.error('Push messaging not supported')
  }
  if (!('Notification' in window)) {
    console.error('Notifications not supported')
  }

  // Listen for messages from service worker (for background notification clicks)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        router.push(event.data.url)
      }
    })
  }

  // Handle app state changes for Capacitor (when app comes to foreground)
  if (Capacitor.isNativePlatform()) {
    App.addListener('appStateChange', ({ isActive }) => {
      // App state changed
    })

    // Handle OAuth callback from Google sign-in
    App.addListener('appUrlOpen', async (event) => {
      // Handle OAuth callback from Google sign-in
      if (
        event.url.includes('oauth-callback') ||
        event.url.includes('access_token') ||
        event.url.includes('code=')
      ) {
        // Close the browser if it's still open
        try {
          await Browser.close()
        } catch (error) {
          // Browser might already be closed, ignore error
        }

        // Supabase OAuth uses URL hash fragments (#) not query parameters
        const url = new URL(event.url)
        const hash = url.hash.substring(1) // Remove the # symbol
        const params = new URLSearchParams(hash)

        const accessToken = params.get('access_token')
        const refreshToken = params.get('refresh_token')
        const error = params.get('error')
        const errorDescription = params.get('error_description')

        if (error) {
          console.error('OAuth error:', error, errorDescription)
          return
        }

        // If we have tokens in the URL hash, set the session
        if (accessToken && refreshToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })

          if (sessionError) {
            console.error('Error setting session:', sessionError)
          } else {
            // Session set successfully, auth state change listener will handle the rest
            console.log('OAuth login successful')
          }
        } else {
          console.warn('[OAuth Callback] Missing tokens in URL')
        }
      } else {
        console.warn('[OAuth Callback] URL does not match OAuth patterns')
        // Handle other deep links if needed
        // You could parse the URL and navigate accordingly
      }
    })

    // Set up Capacitor push notification listeners
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // For foreground notifications on mobile, you could show an in-app notification here
      // The actual notification display is handled by the system
    })

    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      // Handle deep linking when notification is tapped
      const data = notification.notification.data
      if (data?.url) {
        router.push(data.url)
      } else {
        // Fallback to home if no URL provided
        router.push('/')
      }
    })
  }

  // Override VitePWA service worker registration to use module type (only once)
  if (import.meta.env.DEV && 'serviceWorker' in navigator) {
    // Wait for VitePWA to attempt registration, then override it
    const originalRegister = navigator.serviceWorker.register
    navigator.serviceWorker.register = function (scriptURL, options = {}) {
      if (scriptURL.toString().includes('dev-sw.js')) {
        options.type = 'module'
      }
      return originalRegister.call(this, scriptURL, options)
    }
  }

  watch(
    user,
    (u) => {
      if (u.status === 'SignedIn') {
        Sentry.setUser({
          id: u.data.id,
          username: u.data.username,
          dark_mode: u.data.dark_mode
        })
      } else {
        Sentry.setUser(null)
      }
    },
    {
      immediate: true
    }
  )

  // Watch for notification settings changes
  watch(
    () => (user.value.status === 'SignedIn' ? user.value.data.notifications_enabled : undefined),
    (newNotificationsEnabled) => {
      if (newNotificationsEnabled !== undefined && user.value.status === 'SignedIn') {
        notificationsSettings.syncWithDatabase()
      }
    }
  )

  // Check for devtool user switch on initialization (development only)
  let devtoolUserId: any = null
  let devtoolUserEmail: any = null

  if (import.meta.env.DEV) {
    devtoolUserId = useLocalStorage<string | null>('devtool-switch-user', null)
    devtoolUserEmail = useLocalStorage<string | null>('devtool-user-email', null)

    // Initialize with devtool user if available
    if (devtoolUserId.value && devtoolUserEmail.value) {
      console.log(`Devtool: Switching to user ${devtoolUserEmail.value} (${devtoolUserId.value})`)
      // Load the user's data directly
      fetchProfile(devtoolUserId.value)
    }
  }

  // Initialize auth state change listener immediately (not in onMounted)
  supabase.auth.onAuthStateChange((event, session) => {
    // Only sign out if the event is SIGNED_OUT
    if (event === 'SIGNED_OUT') {
      user.value = { status: 'SignedOut' }
      return
    }

    // Check if we have a devtool user switch active - if so, don't override it (development only)
    if (import.meta.env.DEV && devtoolUserId?.value && devtoolUserEmail?.value) {
      console.log('Devtool user active, skipping auth state change')
      return
    }

    // If we have a session, we're signed in
    if (session && (user.value.status !== 'SignedIn' || loadedUserId.value !== session.user.id)) {
      loadedUserId.value = session.user.id
      if (Capacitor.getPlatform() !== 'web') {
        registerNotifications()
        addListeners(session.user.id)
        getDeliveredNotifications()
      } else {
        const firebaseMessaging = getMessaging(firebaseApp)

        // Handle foreground messages
        onMessage(firebaseMessaging, (payload) => {
          // Display notification even when app is in foreground
          if (payload.notification) {
            const notificationTitle = payload.notification.title || 'New Message'
            const notificationOptions = {
              body: payload.notification.body || 'You have a new message',
              icon: payload.notification.image || 'https://gaze.lindsaykwardell.com/logo.png',
              badge: 'https://gaze.lindsaykwardell.com/logo.png',
              tag: 'gaze-notification-foreground',
              requireInteraction: true,
              data: payload.data
            }

            // Check if we have permission to show notifications
            if (Notification.permission === 'granted') {
              const notification = new Notification(notificationTitle, notificationOptions)

              // Handle notification click
              notification.onclick = () => {
                notification.close()
                // Focus the window if it's not already focused
                window.focus()

                // Navigate to the URL if provided in the payload data
                if (payload.data?.url) {
                  router.push(payload.data.url)
                }
              }

              // Auto-close notification after 5 seconds
              setTimeout(() => {
                notification.close()
              }, 5000)
            }
          }
        })

        Notification.requestPermission().then(async (permission) => {
          // Requesting web notification permission
          if (permission === 'granted') {
            // Wait for service worker to be ready and get the correct registration

            const readyRegistration = await navigator.serviceWorker.ready

            // Get the specific registration for our scope
            const swRegistration = await navigator.serviceWorker.getRegistration('/')

            if (!swRegistration || !swRegistration.active) {
              const registrations = await navigator.serviceWorker.getRegistrations()
              registrations.forEach((reg, index) => {})
              return
            }

            // Ensure the service worker is active
            if (!swRegistration.active) {
              // Wait a bit for activation
              await new Promise((resolve) => setTimeout(resolve, 1000))
            }

            // Debug Firebase messaging state

            // Validate VAPID key format
            const vapidKey =
              'BOjclw3cu28M5-WFdoyDi_QAvRgq2iBnVzV5dNBZEdQAfIbmvgKNeax118bFkTlBmc36c3UCnTn69TnLAjaMU38'

            // Let's also try to test the VAPID key validity
            try {
              // Test if we can decode the VAPID key (it should be base64url)
              const keyBuffer = Uint8Array.from(
                atob(vapidKey.replace(/-/g, '+').replace(/_/g, '/')),
                (c) => c.charCodeAt(0)
              )
            } catch (decodeError) {
              console.error('VAPID key decode failed:', decodeError)
            }

            // Add timeout to detect hanging getToken calls
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('getToken timeout after 10 seconds')), 10000)
            })

            try {
              // Give the service worker a moment to fully initialize Firebase
              await new Promise((resolve) => setTimeout(resolve, 2000))

              let currentToken: string | null = null
              try {
                currentToken = (await Promise.race([
                  getToken(firebaseMessaging, {
                    vapidKey,
                    serviceWorkerRegistration: swRegistration
                  }),
                  timeoutPromise
                ])) as string | null
              } catch (method1Error) {
                // Try without explicit service worker registration
                currentToken = (await Promise.race([
                  getToken(firebaseMessaging, {
                    vapidKey
                  }),
                  timeoutPromise
                ])) as string | null
              }

              if (currentToken) {
                await registerToken(session.user.id, currentToken)
              } else {
              }
            } catch (err) {
              console.error('Error occurred while retrieving token:', err)
              if (err instanceof Error) {
                console.error('Error name:', err.name)
                console.error('Error message:', err.message)
                if ('code' in err) console.error('Error code:', (err as any).code)
              }
            } finally {
            }
          }
        })
      }

      fetchProfile(session.user.id)
      notificationsChannel?.unsubscribe()
      notificationsChannel = supabase
        .channel('schema-db-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notification_details',
            filter: `user_id=eq.${session.user.id}`
          },
          (payload) => {
            notifications.addNotification(payload.new as any)
          }
        )
        .subscribe()
    }
  })

  async function fetchProfile(user_id: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'user_id, email, username, avatar, banner_image, dark_mode, reduced_motion, language, notifications_enabled, user_created_at, subscription_tier, subscription_status, has_used_free_trial, needs_onboarding, beta_code_used'
      )
      .eq('user_id', user_id)

    if (error) {
      throw error
    }

    if (data.length === 1) {
      const profile = data[0]!
      user.value = {
        status: 'SignedIn',
        data: {
          id: profile.user_id,
          email: profile.email,
          username: profile.username,
          avatar: profile.avatar,
          banner_image: profile.banner_image,
          dark_mode: profile.dark_mode,
          reduced_motion: profile.reduced_motion,
          language: profile.language,
          notifications_enabled: profile.notifications_enabled,
          user_created_at: profile.user_created_at,
          subscription_tier: profile.subscription_tier,
          subscription_status: profile.subscription_status,
          has_used_free_trial: profile.has_used_free_trial,
          needs_onboarding: profile.needs_onboarding,
          beta_code_used: profile.beta_code_used
        }
      }

      // If user needs onboarding, redirect to welcome flow
      if (profile.needs_onboarding) {
        router.push('/welcome')
      }
      notifications.fetchNotifications()
      photos.fetchMyPhotos()
    } else if (data.length === 0) {
      // Profile doesn't exist - this might be a new OAuth user
      // Try to create user_settings for them
      const wasCreated = await ensureUserSettings(user_id)
      // Fetch profile again after creating settings
      await fetchProfile(user_id)
    }
  }

  async function ensureUserSettings(user_id: string): Promise<boolean> {
    // Check if user_settings already exists
    const { data: existingSettings } = await supabase
      .from('user_settings')
      .select('user_id')
      .eq('user_id', user_id)
      .single()

    if (existingSettings) {
      return false // User settings already exist
    }

    // Get user info from auth - use admin API to get user by ID
    // First try to get current user
    const {
      data: { user: authUser }
    } = await supabase.auth.getUser()

    // If the current user doesn't match, we can't proceed (this shouldn't happen in normal flow)
    if (!authUser || authUser.id !== user_id) {
      console.warn('Cannot create user_settings: auth user mismatch or not found')
      return false
    }

    // Generate username from email (remove @domain part)
    let username = authUser.email?.split('@')[0] || `user_${user_id.slice(0, 8)}`
    // Clean username to match validation pattern
    username = username.replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase()

    // Ensure username doesn't start with a special character
    if (!/^[a-zA-Z0-9]/.test(username)) {
      username = `user_${username}`
    }

    // Check if username is available, if not add a suffix
    let finalUsername = username
    let attempts = 0
    while (attempts < 10) {
      const { count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .ilike('username', finalUsername)

      if (count === 0) {
        break // Username is available
      }

      finalUsername = `${username}_${Math.floor(Math.random() * 10000)}`
      attempts++
    }

    // Detect OS preferences
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Create user_settings with needs_onboarding set to true for new OAuth users
    const { error } = await supabase.from('user_settings').insert({
      user_id: user_id,
      username: finalUsername,
      dark_mode: prefersDark,
      reduced_motion: prefersReducedMotion,
      notifications_enabled: true,
      needs_onboarding: true
    })

    if (error) {
      console.error('Error creating user_settings for OAuth user:', error)
      throw error
    }

    return true // Settings were created
  }

  async function logout() {
    // Remove FCM token from database if we have one stored
    if (currentDeviceToken.value && user.value.status === 'SignedIn') {
      try {
        await supabase.from('fcm_tokens').delete().eq('token', currentDeviceToken.value)

        // Clear the stored token
        currentDeviceToken.value = null
      } catch (error) {
        console.error('Error removing FCM token:', error)
        // Continue with logout even if token removal fails
      }
    }

    // Clear all session data
    user.value = { status: 'SignedOut' }
    notificationsChannel?.unsubscribe()
    notificationsChannel = null

    // Clear photos and notifications
    photos.reset()
    notifications.reset()

    // Sign out from Supabase
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Error logging out:', error)
      throw error
    }

    router.push('/login')
  }

  const addListeners = async (user_id: string) => {
    await PushNotifications.addListener('registration', async (token) => {
      await registerToken(user_id, token.value)
    })

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error: ', err.error)
    })

    // Note: pushNotificationReceived and pushNotificationActionPerformed
    // listeners are now handled in the main store function
  }

  const registerNotifications = async () => {
    let permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!')
    }

    await PushNotifications.register()
  }

  const getDeliveredNotifications = async () => {
    const notificationList = await PushNotifications.getDeliveredNotifications()
  }

  async function registerToken(user_id: string, token: string) {
    // Store the token for later removal during logout
    currentDeviceToken.value = token

    // Check if token already exists
    const { data, error } = await supabase
      .from('fcm_tokens')
      .select('*')
      .eq('user_id', user_id)
      .eq('token', token)

    if (error) {
      console.error('Error checking for existing token: ', error)
      return
    }

    if (data && data.length > 0) {
      // Update token
      await supabase
        .from('fcm_tokens')
        .update({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
        .eq('user_id', user_id)
        .eq('token', token)
    } else {
      // Insert new token
      await supabase.from('fcm_tokens').insert({
        user_id,
        token: token,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    }
  }

  return { api: supabase, user, notifications, fetchProfile, logout }
})
